import React from "react";
import { Button, Input, Select, Switch } from "antd";
import { CloseCircleOutlined, PlusOutlined } from "@ant-design/icons";
import trans from "../../../../utils";

const OPERATORS = [
	{ label: trans("Equals"), value: "equals" },
	{ label: trans("Not Equals"), value: "notEquals" },
	{ label: trans("In"), value: "in" },
	{ label: trans("Not In"), value: "notIn" },
	{ label: trans("Exists (has a value)"), value: "exists" },
	{ label: trans("Does Not Exist (no value)"), value: "notExists" },
];

const VALUELESS_OPERATORS = ["exists", "notExists"];

const COMBINATORS = [
	{ label: trans("All conditions"), value: "AND" },
	{ label: trans("Any condition"), value: "OR" },
];

const EMPTY_CONDITION = { field: null, operator: "equals", value: "" };

export default function FormOptionVisibility({ activeItem, siblingFields, updateActiveItem }) {
	const normalizeRules = () => {
		const rules = activeItem?.visibilityRules;
		if (!rules) return { operator: "AND", conditions: [] };
		if (rules.showWhen) {
			return { operator: "AND", conditions: [{ ...rules.showWhen }] };
		}
		return {
			operator: rules.operator || "AND",
			conditions: Array.isArray(rules.conditions) ? rules.conditions : [],
		};
	};

	const { operator, conditions } = normalizeRules();

	const fieldOptions = siblingFields.map((f) => ({
		label: f.title || f.key,
		value: f.key,
	}));

	const updateRules = (nextOperator, nextConditions) => {
		if (!nextConditions || nextConditions.length === 0) {
			updateActiveItem("visibilityRules", { operator: nextOperator, conditions: [] });
			return;
		}
		updateActiveItem("visibilityRules", {
			operator: nextOperator,
			conditions: nextConditions,
		});
	};

	const addCondition = () => {
		updateRules(operator, [...conditions, { ...EMPTY_CONDITION }]);
	};

	const removeCondition = (index) => {
		updateRules(
			operator,
			conditions.filter((_, i) => i !== index)
		);
	};

	const updateCondition = (index, key, value) => {
		const next = conditions.map((condition, i) => {
			if (i !== index) return condition;
			if (key === "field") {
				return { ...condition, field: value };
			}
			if (key === "operator") {
				return {
					...condition,
					operator: value,
					value: VALUELESS_OPERATORS.includes(value)
						? undefined
						: value === "in" || value === "notIn"
						? Array.isArray(condition.value) && condition.value.length > 0
							? condition.value
							: []
						: "",
				};
			}
			return { ...condition, [key]: value };
		});
		updateRules(operator, next);
	};

	const renderValueInput = (condition, index) => {
		const isMulti = condition.operator === "in" || condition.operator === "notIn";
		if (isMulti) {
			return (
				<Select
					mode="tags"
					style={{ width: "100%" }}
					value={Array.isArray(condition.value) ? condition.value : []}
					onChange={(v) => updateCondition(index, "value", v)}
					placeholder={trans("Enter values")}
					allowClear
				/>
			);
		}
		return (
			<Input
				style={{ width: "100%" }}
				value={condition.value}
				onChange={(e) => updateCondition(index, "value", e.target.value)}
				placeholder={trans("Enter value")}
			/>
		);
	};

	return (
		<div className="field-settings-section">
			<h5 className="field-settings-section-title">{trans("Visibility Rules")}</h5>

			<div className="field-settings-control">
				<label className="form-label">{trans("Show field when")}</label>
				<Select
					style={{ width: "100%" }}
					value={conditions.length > 1 ? operator : "AND"}
					options={COMBINATORS}
					onChange={(v) => updateRules(v, conditions)}
					disabled={conditions.length <= 1}
				/>
			</div>

			{conditions.length === 0 && (
				<div
					style={{
						padding: "10px 12px",
						borderRadius: 6,
						background: "#f8fafc",
						color: "#64748b",
						fontSize: 13,
						marginBottom: 8,
					}}
				>
					{trans("No conditions set. The field is always visible.")}
				</div>
			)}

			{conditions.map((condition, index) => (
				<div
					key={index}
					style={{
						position: "relative",
						padding: 12,
						marginBottom: 10,
						border: "1px solid #e2e8f0",
						borderRadius: 8,
						background: "#fff",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							marginBottom: 10,
						}}
					>
						<span
							style={{
								fontSize: 12,
								fontWeight: 600,
								color: "#334155",
							}}
						>
							{trans("Condition")} {index + 1}
						</span>
						<Button
							type="text"
							danger
							size="small"
							icon={<CloseCircleOutlined />}
							onClick={() => removeCondition(index)}
						/>
					</div>

					<div className="field-settings-control" style={{ marginBottom: 8 }}>
						<label className="form-label">{trans("Field")}</label>
						<Select
							style={{ width: "100%" }}
							value={condition.field}
							options={fieldOptions}
							onChange={(v) => updateCondition(index, "field", v)}
							placeholder={trans("Select a field")}
							allowClear
						/>
					</div>

					<div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
						<div className="field-settings-control" style={{ flex: 1, marginBottom: 0 }}>
							<label className="form-label">{trans("Operator")}</label>
							<Select
								style={{ width: "100%" }}
								value={condition.operator}
								options={OPERATORS}
								onChange={(v) => updateCondition(index, "operator", v)}
								placeholder={trans("Operator")}
							/>
						</div>
					</div>

					{!VALUELESS_OPERATORS.includes(condition.operator) && (
						<div className="field-settings-control" style={{ marginBottom: 0 }}>
							<label className="form-label">{trans("Value")}</label>
							{renderValueInput(condition, index)}
						</div>
					)}
				</div>
			))}

			<Button type="dashed" block icon={<PlusOutlined />} onClick={addCondition}>
				{trans("Add Condition")}
			</Button>

			<div className="field-settings-switch-row" style={{ marginTop: 12 }}>
				<label className="form-label">{trans("Required When Visible")}</label>
				<Switch
					checked={activeItem?.requiredWhenVisible || false}
					onChange={(v) => updateActiveItem("requiredWhenVisible", v)}
				/>
			</div>
		</div>
	);
}
